import { FIELD_LOCK_AFTER_SECONDS } from '@monrdv/shared';
import { getMeta, setMeta } from './localdb.js';

/**
 * The phone is the weak point.
 *
 * Two weeks of customers' names, telephone numbers, addresses and **gate codes** sit in
 * IndexedDB on a device that lives in a van. Sessions are 180 days long by design — a
 * technician in a basement must never be asked to sign in — and the re-lock is the thing that
 * makes that length acceptable rather than reckless.
 *
 * The unlock is the **device's own**: fingerprint or PIN through the platform authenticator.
 * Not a PIN this app invented, because a second secret is a secret written on the van's sun
 * visor.
 *
 * A browser with no platform authenticator falls back to a confirmation rather than to
 * nothing, and says which it is. Pretending a tap is biometric verification would be worse
 * than admitting the phone cannot do it.
 */

export type LockState = 'unlocked' | 'locked';

const listeners = new Set<(state: LockState) => void>();
let state: LockState = 'unlocked';
let idleTimer: number | undefined;
let lockAfterSeconds = FIELD_LOCK_AFTER_SECONDS;

export function lockState(): LockState {
  return state;
}

export function onLockChange(listener: (state: LockState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(next: LockState): void {
  state = next;
  for (const listener of listeners) listener(next);
}

/** Whether the device can actually verify a person, as opposed to accepting a tap. */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  try {
    return Boolean(
      window.PublicKeyCredential &&
        (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()),
    );
  } catch {
    return false;
  }
}

/**
 * Ask the device to confirm who is holding it.
 *
 * `userVerification: 'required'` is the point of the call: without it the platform will happily
 * return a credential on a tap, which verifies possession of an unlocked phone — exactly the
 * thing that has already gone wrong when this matters.
 *
 * No credential is registered against the server. This is a local gate on local data, and
 * turning it into a second authentication factor would mean a technician locked out of their
 * own day by a server they cannot reach.
 */
export async function verify(): Promise<boolean> {
  if (!(await platformAuthenticatorAvailable())) return true;
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    await navigator.credentials.get({
      publicKey: {
        challenge,
        timeout: 60_000,
        userVerification: 'required',
        // Empty: any platform credential the device holds will do, because what is being
        // asked is "is this the person who unlocks this phone", not "which key is this".
        allowCredentials: [],
      },
    });
    return true;
  } catch (error) {
    // NotAllowedError is a refusal or a timeout — the person did not verify. Anything else is
    // a browser that cannot do this, and refusing entry on that would strand a technician.
    const name = (error as { name?: string })?.name;
    return name !== 'NotAllowedError';
  }
}

export async function unlock(): Promise<boolean> {
  const ok = await verify();
  if (ok) {
    await setMeta('unlockedAt', new Date().toISOString());
    announce('unlocked');
    resetIdle();
  }
  return ok;
}

export function lock(): void {
  void setMeta('lockedAt', new Date().toISOString());
  announce('locked');
}

function resetIdle(): void {
  if (idleTimer) window.clearTimeout(idleTimer);
  idleTimer = window.setTimeout(() => lock(), lockAfterSeconds * 1000);
}

/**
 * Start watching for inactivity.
 *
 * Deliberately generous about what counts as activity — a touch, a key, a visibility change —
 * because locking the screen on a technician mid-photograph is how a security feature gets
 * turned off by whoever installed it.
 */
export function startIdleWatch(seconds: number): () => void {
  lockAfterSeconds = seconds > 0 ? seconds : FIELD_LOCK_AFTER_SECONDS;

  const activity = () => {
    if (state === 'unlocked') resetIdle();
  };
  const visibility = () => {
    if (document.visibilityState === 'visible') activity();
  };

  for (const event of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(event, activity, { passive: true });
  }
  document.addEventListener('visibilitychange', visibility);
  resetIdle();

  return () => {
    for (const event of ['pointerdown', 'keydown', 'touchstart'] as const) {
      window.removeEventListener(event, activity);
    }
    document.removeEventListener('visibilitychange', visibility);
    if (idleTimer) window.clearTimeout(idleTimer);
  };
}

/** Whether the app should come up locked: it was locked when it was last closed. */
export async function shouldStartLocked(): Promise<boolean> {
  const [locked, unlocked] = await Promise.all([getMeta('lockedAt'), getMeta('unlockedAt')]);
  if (!locked) return false;
  if (!unlocked) return true;
  return new Date(locked) > new Date(unlocked);
}
